import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-sonner-toast';
import { db } from '@/lib/firebase/firestore';
import { collection, addDoc } from 'firebase/firestore';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (confirmText.toLowerCase() !== 'delete') {
      toast({
        title: 'Error',
        description: 'Please type "delete" to confirm account deletion',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save the deletion request to the help_reports collection
      if (currentUser) {
        await addDoc(collection(db, 'help_reports'), {
          type: 'account_deletion',
          userId: currentUser.uid,
          userEmail: currentUser.email,
          reason: reason || 'No reason provided',
          timestamp: new Date(),
          status: 'new',
          category: 'account',
        });

        // Show success message
        toast({
          title: 'Account Deletion Requested',
          description:
            "Your account is up for deletion. You'll receive an email when it's complete.",
        });

        onClose();
      } else {
        throw new Error('No user is currently signed in');
      }
    } catch (error) {
      console.error('Error submitting account deletion request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit deletion request. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form fields when modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setReason('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">Request Account Deletion</DialogTitle>
          <DialogDescription>
            Your request will be reviewed by our team. Once approved, all your data will be
            permanently deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Type "delete" to confirm account deletion:</p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="delete"
              className="border-destructive/50 focus-visible:ring-destructive"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Why are you leaving? (optional)</p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please let us know why you're deleting your account"
              className="min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={confirmText.toLowerCase() !== 'delete' || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Request Account Deletion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountModal;
